/**
 * InterfacrIntegration.ts - Interfacr integration for Stylr
 * 
 * CHANGE HISTORY:
 * - 251102_1348 danielbeer: Created to fix missing orchestrator integration
 */

/**
 * Register Stylr with Interfacr
 * 
 * This allows other parts of the app to open Stylr through Interfacr:
 * 
 * await interfacr.execute({
 *   type: 'stylr',
 *   operation: 'openStyleManager',
 *   params: { objectType: 'card', objectId: '123' }
 * });
 */
export function registerStylrWithInterfacr(_stylrManager: any, interfacr: any): void {
  if (!interfacr) {
    console.warn('[Stylr] Interfacr not available, skipping registration');
    return;
  }

  console.log('[Stylr] Registering with Interfacr');

  // Register Stylr as an interface type
  interfacr.registerInterface('stylr', {
    // Open Style Manager
    openStyleManager: async (params: any) => {
      console.log('[Stylr] Interfacr: openStyleManager', params);
      
      // This would typically open the StyleManager component
      // For now, we'll return the configuration
      return {
        component: 'StyleManager',
        props: {
          initialScope: params.scope || 'global',
          initialObjectType: params.objectType,
          initialObjectId: params.objectId
        }
      };
    },

    // Open Image Library
    openImageLibrary: async (params: any) => {
      console.log('[Stylr] Interfacr: openImageLibrary', params);
      
      return {
        component: 'ImageLibrary',
        props: {
          onSelectImage: params.onSelectImage
        }
      };
    },

    // Show context menu
    showContextMenu: async (params: any) => {
      console.log('[Stylr] Interfacr: showContextMenu', params);
      
      return {
        component: 'StylrContextMenu',
        props: {
          x: params.x,
          y: params.y,
          object: params.object
        }
      };
    },

    // Edit style (opens StyleManager with specific object)
    editStyle: async (params: any) => {
      console.log('[Stylr] Interfacr: editStyle', params);
      
      return {
        component: 'StyleManager',
        props: {
          initialScope: 'object',
          initialObjectType: params.objectType,
          initialObjectId: params.objectId,
          editMode: 'edit'
        }
      };
    }
  });

  // Register routes
  interfacr.registerRoute('/stylr', {
    component: 'StyleManager',
    title: 'Style Manager'
  });

  interfacr.registerRoute('/stylr/images', {
    component: 'ImageLibrary',
    title: 'Image Library'
  });

  interfacr.registerRoute('/stylr/themes', {
    component: 'ThemeEditor',
    title: 'Theme Editor'
  });

  console.log('[Stylr] Successfully registered with Interfacr');
}

/**
 * Helper to open StyleManager through Interfacr
 */
export async function openStyleManager(
  interfacr: any,
  options: {
    objectType?: string;
    objectId?: string;
    scope?: 'global' | 'object';
  } = {}
) {
  if (!interfacr) {
    console.error('[Stylr] Cannot open StyleManager: Interfacr not available');
    return null;
  }

  return await interfacr.execute({
    type: 'stylr',
    operation: 'openStyleManager',
    params: options
  });
}

/**
 * Helper to open Image Library through Interfacr
 */
export async function openImageLibrary(
  interfacr: any,
  onSelectImage?: (image: any) => void
) {
  if (!interfacr) {
    console.error('[Stylr] Cannot open Image Library: Interfacr not available');
    return null;
  }

  return await interfacr.execute({
    type: 'stylr',
    operation: 'openImageLibrary',
    params: { onSelectImage }
  });
}

/**
 * Helper to show context menu through Interfacr
 */
export async function showStylrContextMenu(
  interfacr: any,
  x: number,
  y: number,
  object: any
) {
  if (!interfacr) {
    console.error('[Stylr] Cannot show context menu: Interfacr not available');
    return null;
  }

  return await interfacr.execute({
    type: 'stylr',
    operation: 'showContextMenu',
    params: { x, y, object }
  });
}
